/**
 * `<RequireAuth>` — gate child routes on a live session, expose session
 * to descendants via React Context (web UI Phase 2 chunk 4/6).
 *
 * Loading: render a tiny placeholder. We don't ship a real skeleton at
 * Phase 2 chunk 4 — the network round-trip is one-shot and same-origin;
 * if it ever stretches past ~200 ms we'll revisit.
 * No session: redirect to `/auth/request` (Navigate, replace) so the
 * back button doesn't trap the captain on the gated page.
 * Has session: render children + provide it via context so leaf pages
 * (like Setup) don't all re-call `useSession`.
 */

import { createContext, useContext, type ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useSession, type Session } from "../lib/session";

const AuthedSessionContext = createContext<Session | null>(null);

export function useAuthedSession(): Session {
  const ctx = useContext(AuthedSessionContext);
  if (!ctx) {
    // This indicates a wiring bug — RequireAuth is the only thing that
    // populates the context, and it never renders children with a null
    // session. Surface the bug rather than letting it silently fan out.
    throw new Error(
      "useAuthedSession() called outside <RequireAuth>; wrap the route in <RequireAuth>.",
    );
  }
  return ctx;
}

type Props = {
  children: ReactNode;
};

export default function RequireAuth({ children }: Props) {
  const { session, loading, error } = useSession();

  if (loading) {
    return (
      <section>
        <p className="muted">Loading…</p>
      </section>
    );
  }

  if (session === null) {
    return <Navigate to="/auth/request" replace />;
  }

  if (error) {
    // We still have a session; log + render. (Unreachable in the common
    // path — useSession sets session to null when error fires.)
    // eslint-disable-next-line no-console
    console.warn("[RequireAuth] non-fatal session error:", error);
  }

  return (
    <AuthedSessionContext.Provider value={session}>
      {children}
    </AuthedSessionContext.Provider>
  );
}
